import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useDispatch } from "react-redux";
import config from "../config";
import Pagination from "../Pagination";
import { addToCart } from "../redux/slice/CartSlice";
import { trackAddToCart } from "../analytics/events";
import noimage from "../image/noimage.jpg";

const SORT_OPTIONS = [
  { value: "newest",     label: "Newest" },
  { value: "price_asc",  label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

function SkeletonCard() {
  return (
    <div className="col-lg-3 col-6">
      <div className="mg-skeleton-card">
        <div className="mg-skeleton mg-skeleton-img"></div>
        <div className="mg-skeleton mg-skeleton-line w-75 mt-2"></div>
        <div className="mg-skeleton mg-skeleton-line w-50 mt-1"></div>
        <div className="mg-skeleton mg-skeleton-line w-25 mt-1"></div>
      </div>
    </div>
  );
}

export default function ProductList() {
  const dispatch    = useDispatch();
  const navigate    = useNavigate();
  const location    = useLocation();
  const { category } = useParams();

  const [products,      setProducts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [totalPages,    setTotalPages]    = useState(1);
  const [totalLength,   setTotalLength]   = useState(0);
  const [sort,          setSort]          = useState("newest");
  const [categoryName,  setCategoryName]  = useState("All Products");
  const [addedKey,      setAddedKey]      = useState(null);

  const fetchProducts = useCallback(async (page, sortVal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page:     page,
        sort:     sortVal,
        ...(category ? { category } : {}),
      });
      const res = await axios.get(`${config.apiUrl}/product?${params}`);
      setProducts(res.data.data || []);
      setTotalLength(res.data.meta?.total || 0);
      setTotalPages(res.data.meta?.last_page || 1);

      // Get category name from first product
      if (res.data.data?.[0]?.category?.title) {
        setCategoryName(res.data.data[0].category.title);
      }
    } catch {
      navigate("/error");
    } finally {
      setLoading(false);
    }
  }, [category, navigate]);

  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1, sort);
    window.scrollTo(0, 0);
  }, [category, sort]);  // eslint-disable-line

  useEffect(() => {
    fetchProducts(currentPage, sort);
    window.scrollTo(0, 0);
  }, [currentPage]); // eslint-disable-line

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSortChange = (e) => {
    setSort(e.target.value);
    setCurrentPage(1);
  };

  const handleQuickAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const firstVariant = product.variants?.[0] || null;
    dispatch(addToCart({ product, variant: firstVariant, quantity: 1 }));
    trackAddToCart(product, firstVariant, 1);
    setAddedKey(product.id);
    setTimeout(() => setAddedKey(null), 1800);
  };

  const pageTitle = `${categoryName} | MeraGym Shop`;

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{pageTitle}</title>
          <meta
            name="description"
            content={`Shop ${categoryName} online at MeraGym. Best prices, fast shipping, and quality guaranteed.`}
          />
          <link
            rel="canonical"
            href={`https://meragym.com/shop/${category || ""}`}
          />
        </Helmet>
      </HelmetProvider>

      <section className="product-list-section">
        <div className="container">
          {/* Header row */}
          <div className="mg-list-header">
            <div>
              <nav className="pd-breadcrumb mb-1" aria-label="breadcrumb">
                <Link to="/shop">Shop</Link>
                <span>/</span>
                <span>{categoryName}</span>
              </nav>
              <h1 className="mg-list-title">{categoryName}</h1>
              {!loading && (
                <p className="mg-list-count">
                  Showing {products.length} of {totalLength} products
                </p>
              )}
            </div>
            <div className="mg-list-sort">
              <label htmlFor="sort-select">Sort by:</label>
              <select
                id="sort-select"
                value={sort}
                onChange={handleSortChange}
                className="mg-sort-select"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Grid */}
          <div className="row product-body g-3 mt-1">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
            ) : products.length === 0 ? (
              <div className="mg-empty-state">
                <i className="fas fa-box-open"></i>
                <p>No products found in this category.</p>
                <Link to="/shop" className="mg-empty-cta">Browse All Products</Link>
              </div>
            ) : (
              products.map((data) => {
                const hasDiscount = Number(data.price) > Number(data.discount_price);
                const discPct = hasDiscount
                  ? Math.round(((data.price - data.discount_price) / data.price) * 100)
                  : 0;
                return (
                  <div className="col-lg-3 col-6" key={data.id}>
                    <Link
                      to={`/shop/${data.category?.slug || category}/${data.slug}`}
                      className="product mg-product-card"
                    >
                      <div className="image">
                        {discPct > 0 && (
                          <span className="mg-discount-badge">-{discPct}%</span>
                        )}
                        <img
                          src={data.thumbnail || noimage}
                          className="img-fluid"
                          alt={data.title}
                          onError={(e) => { e.target.src = noimage; }}
                        />
                      </div>
                      <div className="product-content">
                        <p className="desc">{data.title}</p>
                        <h6 className="cat">{data.category?.title}</h6>
                        <div className="price">
                          <h6 className="sale">
                            ₹{Number(data.discount_price).toLocaleString("en-IN")}
                          </h6>
                          {hasDiscount && (
                            <p className="actual">
                              ₹{Number(data.price).toLocaleString("en-IN")}
                            </p>
                          )}
                        </div>
                        <button
                          className={`mg-quick-add-btn${addedKey === data.id ? " added" : ""}`}
                          onClick={(e) => handleQuickAddToCart(e, data)}
                          aria-label="Add to cart"
                        >
                          {addedKey === data.id ? (
                            <><i className="bi bi-check-lg me-1"></i>Added</>
                          ) : (
                            <><i className="bi bi-cart-plus me-1"></i>Add to Cart</>
                          )}
                        </button>
                      </div>
                    </Link>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="row mt-4">
              <div className="col-lg-12">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
